"use client";

import { useEffect, useState } from "react";

export type PerfLevel = "mobile-static" | "low" | "medium" | "high";

// Resolved on the client only. Server renders `null` until hydration so the
// initial paint matches between server/client (no hydration mismatch).
//
// `reducedMotion` is reported separately from `level` so consumers can keep
// rendering their visual (e.g. a chrome-sphere shader) while pausing motion
// — previously we collapsed reduced-motion users into `mobile-static`,
// which silently removed every 3D scene from the page entirely.
export type PerfState = {
  level: PerfLevel | null;
  no3D: boolean;
  reducedMotion: boolean;
};

function detect(): PerfState {
  if (typeof window === "undefined")
    return { level: null, no3D: false, reducedMotion: false };

  const params = new URLSearchParams(window.location.search);
  const no3D = params.has("no-3d");
  const reducedMotion = window.matchMedia(
    "(prefers-reduced-motion: reduce)",
  ).matches;

  if (no3D) return { level: "mobile-static", no3D: true, reducedMotion };

  // Real-mobile gate: only collapse to the static fallback for narrow
  // viewports. Reduced-motion is now handled per-component (shaders set
  // speed=0, Canvases set frameloop="never") so the visual still renders.
  const small = window.innerWidth < 768;
  if (small) return { level: "mobile-static", no3D: false, reducedMotion };

  const cores = navigator.hardwareConcurrency ?? 4;
  if (cores < 4) return { level: "low", no3D: false, reducedMotion };
  if (cores < 8) return { level: "medium", no3D: false, reducedMotion };
  return { level: "high", no3D: false, reducedMotion };
}

export function usePerfLevel(): PerfState {
  const [state, setState] = useState<PerfState>({
    level: null,
    no3D: false,
    reducedMotion: false,
  });

  useEffect(() => {
    setState(detect());

    // Re-evaluate on viewport changes that cross the mobile threshold AND
    // when the user toggles their OS reduced-motion setting.
    let raf = 0;
    const reEval = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setState(detect()));
    };
    const motionMql = window.matchMedia("(prefers-reduced-motion: reduce)");
    window.addEventListener("resize", reEval);
    motionMql.addEventListener("change", reEval);
    return () => {
      window.removeEventListener("resize", reEval);
      motionMql.removeEventListener("change", reEval);
      cancelAnimationFrame(raf);
    };
  }, []);

  return state;
}
